import type React from 'react';
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import constants from 'app/shared/constants/ConstantsBundle';
import { Button } from '../../../ui/Button';
import { ProblemCircleIcon, ToolsIcon, TrashIcon } from '../../../ui/icons';
import { actionLabel, actionWithLabel } from '../../../../pages/HaloListingPage/desktopStickyHeader.styles';
import * as styles from './listingToolsButton.styles';

type ListingToolsButtonProps = {
  aliasEncoded: string;
  isListingHidden: boolean;
  onReportListing: () => void;
  onToggleHidden: () => void;
  size?: 'sm' | 'md';
};

export const ListingToolsButton = ({
  aliasEncoded,
  isListingHidden,
  onReportListing,
  onToggleHidden,
  size = 'md',
}: ListingToolsButtonProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuItemRefs = useRef<Array<HTMLButtonElement | null>>([]);

  useLayoutEffect(() => {
    if (!isOpen) return;
    menuItemRefs.current[0]?.focus();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (target && containerRef.current && !containerRef.current.contains(target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleMouseDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, [isOpen]);

  const closeAndRestoreFocus = useCallback(() => {
    setIsOpen(false);
    triggerRef.current?.focus();
  }, []);

  const openListingTools = () => {
    window.open(constants.REP_HDP_URI + aliasEncoded, '_blank', 'noopener,noreferrer');
    closeAndRestoreFocus();
  };

  const handleReportListing = () => {
    onReportListing();
    closeAndRestoreFocus();
  };

  const handleToggleHidden = () => {
    onToggleHidden();
    closeAndRestoreFocus();
  };

  const handleBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setIsOpen(false);
    }
  };

  const focusItemAt = (index: number) => {
    const items = menuItemRefs.current;
    if (items.length === 0) return;
    const wrapped = (index + items.length) % items.length;
    items[wrapped]?.focus();
  };

  const handleMenuKeyDown = (event: React.KeyboardEvent<HTMLUListElement>) => {
    const items = menuItemRefs.current;
    const currentIndex = items.findIndex((item) => item === document.activeElement);

    switch (event.key) {
      case 'Escape':
        // Stop here so an ancestor overlay's Escape handler doesn't also close itself.
        event.stopPropagation();
        closeAndRestoreFocus();
        break;
      case 'ArrowDown':
        event.preventDefault();
        focusItemAt(currentIndex + 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        focusItemAt(currentIndex - 1);
        break;
      case 'Home':
        event.preventDefault();
        focusItemAt(0);
        break;
      case 'End':
        event.preventDefault();
        focusItemAt(items.length - 1);
        break;
      default:
        break;
    }
  };

  const setListingToolsItemRef = useCallback((node: HTMLButtonElement | null) => {
    menuItemRefs.current[0] = node;
  }, []);
  const setReportListingItemRef = useCallback((node: HTMLButtonElement | null) => {
    menuItemRefs.current[1] = node;
  }, []);
  const setHideListingItemRef = useCallback((node: HTMLButtonElement | null) => {
    menuItemRefs.current[2] = node;
  }, []);

  return (
    <div ref={containerRef} className={styles.container} onBlur={handleBlur}>
      <div className={actionWithLabel}>
        <Button
          ref={triggerRef}
          shape="circle"
          tone="neutral"
          emphasis="outlined"
          size={size}
          onClick={() => setIsOpen((open) => !open)}
          aria-label="Listing tools"
          aria-haspopup="menu"
          aria-expanded={isOpen}
          aria-controls={menuId}
        >
          <ToolsIcon />
        </Button>
        <span className={actionLabel}>Tools</span>
      </div>

      {isOpen && (
        <ul id={menuId} className={styles.menu} role="menu" aria-label="Listing tools" onKeyDown={handleMenuKeyDown}>
          <li role="none">
            <button
              ref={setListingToolsItemRef}
              type="button"
              className={styles.menuItem}
              role="menuitem"
              onClick={openListingTools}
            >
              <ToolsIcon className={styles.menuItemIcon} />
              <span>Listing tools</span>
            </button>
          </li>
          <li role="none">
            <button
              ref={setReportListingItemRef}
              type="button"
              className={styles.menuItem}
              role="menuitem"
              onClick={handleReportListing}
            >
              <ProblemCircleIcon className={styles.menuItemIcon} />
              <span>Report listing</span>
            </button>
          </li>
          <li role="none">
            <button
              ref={setHideListingItemRef}
              type="button"
              className={styles.menuItem}
              role="menuitem"
              onClick={handleToggleHidden}
            >
              <TrashIcon className={styles.menuItemIcon} />
              <span>{isListingHidden ? 'Unhide listing' : 'Hide listing'}</span>
            </button>
          </li>
        </ul>
      )}
    </div>
  );
};
